import { MunIllustration } from "@/components/design-system/MunIllustration";
import { MunLoading } from "@/components/design-system/MunLoading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getStatusConfig } from "@/lib/order-utils";
import type { OrderStatusEnum } from "api/models";
import { format } from "date-fns";
import fuzzysort from "fuzzysort";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CreditCard,
  Mail,
  Search,
  ShoppingBag,
} from "lucide-react";
import React, { useMemo, useState } from "react";

function DateCell({ date }: { date: string }) {
  const dateObj = new Date(date);
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-sm font-medium text-foreground">
        {format(dateObj, "MMM dd, yyyy")}
      </div>
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <Calendar className="h-3 w-3 shrink-0" />
        {format(dateObj, "HH:mm")}
      </div>
    </div>
  );
}

function StatusCell({ status }: { status: OrderStatusEnum }) {
  const statusConfig = getStatusConfig(status);
  return <Badge className={statusConfig.color}>{statusConfig.label}</Badge>;
}

function PaymentCell({ payment }: { payment: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <CreditCard className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground uppercase tracking-wide">
        {payment}
      </span>
    </div>
  );
}

export function OrdersTable<T>({
  orders,
  pagination,
  isLoading,
  currentPage,
  onPageChange,
  renderTableHeaders,
  renderTableRow,
  renderExpandedContent,
  getOrderId,
  onSendReceipt,
  canSendReceipt,
  searchQuery,
  getSearchableText,
}: {
  orders: T[];
  pagination: { page: number; totalPages: number };
  isLoading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  renderTableHeaders: () => React.ReactNode;
  renderTableRow: (order: T, isExpanded: boolean) => React.ReactNode;
  renderExpandedContent: (order: T) => React.ReactNode;
  getOrderId: (order: T) => string;
  onSendReceipt?: (order: T) => void;
  canSendReceipt?: (order: T) => boolean;
  searchQuery?: string;
  getSearchableText?: (order: T) => string;
}) {
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const filteredOrders = useMemo(() => {
    if (!searchQuery || !getSearchableText) return orders;
    const searchResults = fuzzysort.go(searchQuery, orders, {
      key: getSearchableText,
      threshold: -10000,
    });
    return searchResults.map((result) => result.obj);
  }, [orders, searchQuery, getSearchableText]);

  if (isLoading) return <MunLoading />;
  if (!orders) return null;

  const hasNoResults = filteredOrders.length === 0 && orders.length > 0;
  const isEmpty = orders.length === 0;

  if (isEmpty) {
    return (
      <MunIllustration
        icon={ShoppingBag}
        title="No orders found"
        subtitle="There are no orders to display. Try adjusting your filters or date range."
      />
    );
  }

  if (hasNoResults) {
    return (
      <MunIllustration
        icon={Search}
        title="No matching orders"
        subtitle={`No orders match your search query "${searchQuery}". Try different keywords.`}
      />
    );
  }

  return (
    <div className="border shadow-sm rounded-lg overflow-hidden bg-card">
      <div className="w-full overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden">
            <Table className="w-full min-w-[800px]">
              <TableHeader>
                <TableRow className="bg-muted/50 border-b border-border sticky top-0 z-10">
                  {renderTableHeaders()}
                  <TableHead className="w-[140px] sm:w-[160px] py-4 px-4 text-sm font-semibold text-foreground">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order, index) => {
                  const orderId = getOrderId(order);
                  const isExpanded = expandedOrderId === orderId;
                  const canSend = canSendReceipt?.(order) ?? true;

                  return (
                    <React.Fragment key={orderId}>
                      <TableRow
                        className={`hover:bg-muted/30 transition-colors border-b border-border/50 cursor-pointer ${
                          index % 2 === 1 ? "bg-muted/5" : ""
                        }`}
                        onClick={() =>
                          setExpandedOrderId(isExpanded ? null : orderId)
                        }
                      >
                        {renderTableRow(order, isExpanded)}
                        <TableCell className="py-4 px-4">
                          <div className="flex items-center gap-2 sm:gap-3">
                            {onSendReceipt && (
                              <TooltipProvider>
                                <Tooltip delayDuration={200}>
                                  <TooltipTrigger asChild>
                                    <div
                                      className={
                                        !canSend ? "cursor-not-allowed" : ""
                                      }
                                    >
                                      <Button
                                        size="icon"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onSendReceipt(order);
                                        }}
                                        disabled={!canSend}
                                        className={`h-9 w-9 shrink-0 ${
                                          !canSend ? "cursor-not-allowed" : ""
                                        }`}
                                      >
                                        <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                      </Button>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      {canSend
                                        ? "Send Email Receipt"
                                        : "Email receipt not available for this provider"}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedOrderId(
                                        isExpanded ? null : orderId
                                      );
                                    }}
                                    className="h-9 w-9 shrink-0"
                                  >
                                    {isExpanded ? (
                                      <ChevronUp className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                    ) : (
                                      <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {isExpanded
                                      ? "Collapse details"
                                      : "Expand details"}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow key={`${orderId}-expanded`}>
                          <TableCell
                            colSpan={99}
                            className="bg-muted/10 p-0 border-b border-border/50"
                          >
                            <div className="p-6 sm:p-8 border-l-4 border-l-primary/30">
                              {renderExpandedContent(order)}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      {!isEmpty && !hasNoResults && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-4 sm:px-6 py-4 border-t bg-muted/20">
          <span className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
              className="h-9"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage >= pagination.totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              className="h-9"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-4 w-4 sm:ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export { DateCell, PaymentCell, StatusCell };
